import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getCardImageUrl, makeCardImageErrorHandler } from 'utils/cardImageFallback';
import { useNavigate } from 'react-router';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import { Trade, TradeItem } from '../ActiveTradesPage';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import AddCardsToTradeModal from './AddCardsToTradeModal/AddCardsToTradeModal';
import SendPointsModal from 'components/SendPointsModal/SendPointsModal';
import PackingSlipModal from './PackingSlipModal';
import ShippingCountdown from './ShippingCountdown';
import TradeItemEditRow from './TradeItemEditRow';
import EditTradeItemModal from './EditTradeItemModal';
import {
  useMarkAsShippedMutation,
  useRemoveCardFromTradeMutation,
  useLazyGetTradeItemEditOptionsQuery,
  useUpdateTradeItemMutation,
  EditableTradeItem,
  EditableTradeItemPreferences,
} from 'store/services/trade/tradeApi';
// Removed admin API import - causing 401 for non-admin users
// import { useGetTradeFeeQuery } from 'store/services/admin/adminApi';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import { ContactUserModal } from 'components/ContactUserModal/ContactUserModal';
import { SetIcon } from 'components/SetIcon';
import { FoilCardImage } from 'components/FoilCardImage';
import CardImageTooltip from 'components/CardLink/CardImageTooltip';
import { formatLanguage } from 'constants/languages';
import './TradeCard.css';

interface Props {
  trade: Trade;
}

// Format condition to short acronym
const formatCondition = (condition: string): string => {
  const conditionMap: { [key: string]: string } = {
    'NEAR_MINT': 'NM',
    'NM': 'NM',
    'SLIGHTLY_PLAYED': 'LP',
    'LIGHTLY_PLAYED': 'LP',
    'LP': 'LP',
    'SP': 'LP',
    'MODERATELY_PLAYED': 'MP',
    'MP': 'MP',
    'HEAVILY_PLAYED': 'HP',
    'HP': 'HP',
    'DAMAGED': 'DMG',
    'DMG': 'DMG'
  };
  return conditionMap[condition] || condition;
};

// Simple mobile detection hook
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
}

// Calculate remaining days in the packing slip access window.
// Returns 1-7 (ceiling days remaining), or null if expired/invalid.
function getPackingSlipDaysRemaining(expiresAt?: string | null): number | null {
  if (!expiresAt) return null;
  const expiryDate = new Date(expiresAt);
  if (isNaN(expiryDate.getTime())) return null;
  const remainingMs = expiryDate.getTime() - Date.now();
  if (remainingMs <= 0) return null;
  return Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
}

// Build permissive fallback preferences when want list entry has been deleted.
// The backend skips preference validation in this case, so we allow all standard options.
const buildFallbackPreferences = (
  editOption: EditableTradeItem,
  tradeItem: TradeItem
): EditableTradeItemPreferences => ({
  acceptableSetDetails: [{
    name: tradeItem.set,
    code: tradeItem.setCode || editOption.currentEdition,
    cardId: editOption.cardId,
    imageUrl: tradeItem.image,
    availableInFoil: true,
    availableInNonFoil: true,
  }],
  acceptableConditions: [],
  acceptableLanguages: ['EN', 'FR', 'DE', 'ES', 'IT', 'PT', 'JA', 'KO', 'RU', 'ZHS', 'ZHT'],
  acceptFoil: true,
  acceptNonFoil: true,
});

export default function TradeCard({ trade }: Props) {
  const [isPackingSlipOpen, setIsPackingSlipOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [markAsShipped, { isLoading: isMarkingShipped }] = useMarkAsShippedMutation();
  const [removeCardFromTrade, { isLoading: isRemovingCard }] = useRemoveCardFromTradeMutation();
  const cardImageFallbackRef = useRef<Map<string | number, number>>(new Map());

  // Edit mode state
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [editOptionsCache, setEditOptionsCache] = useState<Map<number, EditableTradeItem> | null>(null);
  const [isLoadingEditOptions, setIsLoadingEditOptions] = useState(false);
  const [triggerGetEditOptions] = useLazyGetTradeItemEditOptionsQuery();
  const [updateTradeItem, { isLoading: isUpdatingItem }] = useUpdateTradeItemMutation();
  const isMobile = useIsMobile();

  const isReady = trade.status === 'ready';
  const packingSlipDaysRemaining = trade.packingSlipAvailable
    ? getPackingSlipDaysRemaining(trade.packingSlipExpiresAt)
    : null;
  const [isAddCardsOpen, setIsAddCardsOpen] = useState(false);
  const [isSendPointsOpen, setIsSendPointsOpen] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<{
    scryfallId?: string;
    imageUrl?: string;
    cardName: string;
    position: { x: number; y: number };
  } | null>(null);
  const handleCardHover = (
    e: React.MouseEvent,
    item: TradeItem,
  ) => {
    setHoveredCard({
      scryfallId: item.scryfallId,
      imageUrl: getCardImageUrl({ image: item.image, scryfallId: item.scryfallId }, 'normal'),
      cardName: item.name,
      position: { x: e.clientX, y: e.clientY },
    });
  };
  const handleCardLeave = () => setHoveredCard(null);

  const handleEditClick = useCallback(async (index: number) => {
    // Cancel any current edit
    if (editingItemIndex !== null && editingItemIndex !== index) {
      setEditingItemIndex(null);
    }

    // If we already have cached options, just enter edit mode
    if (editOptionsCache) {
      // Fill in fallback preferences if want list entry was deleted
      const cached = editOptionsCache.get(index);
      if (cached && !cached.preferences) {
        const updated = new Map(editOptionsCache);
        updated.set(index, {
          ...cached,
          preferences: buildFallbackPreferences(cached, trade.items[index]),
        });
        setEditOptionsCache(updated);
      }
      setEditingItemIndex(index);
      return;
    }

    // Fetch edit options
    setIsLoadingEditOptions(true);
    try {
      const result = await triggerGetEditOptions({ tradeId: trade.id }).unwrap();
      // Build map keyed by itemIndex to correctly correlate with trade.items
      const optionsMap = new Map<number, EditableTradeItem>();
      result.items.forEach((item, idx) => {
        const key = item.itemIndex ?? idx;
        const tradeItem = trade.items[key];
        if (!item.preferences && tradeItem) {
          optionsMap.set(key, { ...item, preferences: buildFallbackPreferences(item, tradeItem) });
        } else {
          optionsMap.set(key, item);
        }
      });
      setEditOptionsCache(optionsMap);
      setEditingItemIndex(index);
    } catch (error: any) {
      console.error('Failed to fetch edit options:', error);
      toast.error(error?.data?.message || 'Unable to load edit options. Please try again.');
    } finally {
      setIsLoadingEditOptions(false);
    }
  }, [editingItemIndex, editOptionsCache, triggerGetEditOptions, trade.id, trade.items]);

  const handleEditSave = useCallback(async (
    itemId: string,
    updates: { cardId: number; condition: string; language: string; foil: boolean }
  ) => {
    try {
      const result = await updateTradeItem({
        tradeId: trade.id,
        itemId,
        ...updates,
      }).unwrap();

      if (result.success) {
        const itemName = editingItemIndex !== null ? trade.items[editingItemIndex]?.name : undefined;
        const deltaText = result.totalDelta !== 0
          ? ` (${result.totalDelta > 0 ? '+' : ''}${result.totalDelta} points)`
          : '';
        toast.success(`${itemName || 'Item'} updated successfully${deltaText}`);
        setEditingItemIndex(null);
        setEditOptionsCache(null); // Clear cache so it refetches with updated data
      } else {
        toast.error(result.message || 'Failed to update item');
      }
    } catch (error: any) {
      console.error('Failed to update trade item:', error);
      const errorMsg = error?.data?.message || 'Failed to update item. Please try again.';
      toast.error(errorMsg);
      // Keep edit mode open so user can adjust
    }
  }, [updateTradeItem, trade.id, editingItemIndex, trade.items]);

  const handleEditCancel = useCallback(() => {
    setEditingItemIndex(null);
  }, []);

  const handleContactClick = () => {
    setIsContactModalOpen(true);
  };

  const handleSayThanksClick = () => {
    setIsContactModalOpen(true);
  };
  const navigate = useNavigate();
  
  // Use the trade fee from the trade data itself, or default to 1%
  // Don't fetch from admin API - causes 401 for non-admin users
  const feePercentage = trade.tradeFee ?
    Math.round((trade.tradeFee / trade.totalValue) * 100) : 1;

  const handleMarkAsShipped = async () => {
    try {
      const result = await markAsShipped({ tradeId: trade.id }).unwrap();
      if (result.success) {
        toast.success('Trade marked as shipped successfully!');
      } else {
        toast.error(result.message || 'Failed to mark trade as shipped');
      }
    } catch (error: any) {
      console.error('Error marking trade as shipped:', error);
      toast.error(error?.data?.message || 'Failed to mark trade as shipped');
    }
  };

  const handleRemoveCard = async (item: any, itemIndex: number) => {
    // Only allow removing cards from trades that are ready (not shipped or completed)
    if (trade.status !== 'ready') {
      return;
    }

    try {
      const result = await removeCardFromTrade({
        tradeId: trade.id,
        itemIndex
      }).unwrap();

      if (result.success) {
        toast.success(`${item.name} removed from trade. ${result.refundedPoints || 0} points refunded to receiver.`);
      } else {
        toast.error(result.message || 'Failed to remove card from trade');
      }
    } catch (error: any) {
      console.error('Error removing card from trade:', error);
      toast.error(error?.data?.message || 'Failed to remove card from trade');
    }
  };

  const handleTradeAgain = () => {
    // Navigate to /sendcards with the trader's username as search filter
    navigate(`/sendcards?search=${encodeURIComponent(trade.traderName)}`);
  };

  const getStatusTitle = () => {
    switch (trade.status) {
      case 'ready':
        return 'Ready to ship';
      case 'transit':
        return 'Shipped';
      case 'completed':
        return 'Arrived';
      case 'cancelled':
        return trade.isRemovedItem ? 'Removed' : 'Cancelled';
      default:
        return '';
    }
  };

  const getStatusLabel = () => {
    switch (trade.status) {
      case 'ready':
        return 'You will mail:';
      case 'transit':
        return 'You sent:';
      case 'completed':
        return 'You sent:';
      case 'cancelled':
        return trade.isRemovedItem ? 'You were sending:' : 'You were sending:';
      default:
        return '';
    }
  };


  const renderStarRating = (rating: number) => {
    // Ensure rating is between 0 and 5
    const safeRating = Math.max(0, Math.min(5, rating || 0));
    const emptyStars = Math.max(0, 5 - safeRating);
    return '★'.repeat(safeRating) + '☆'.repeat(emptyStars);
  };

  return (
    <div className="trade-card">
      <div className="trade-card-left">
        <div className="package-title-row">
          <h3 className="trade-status-title">{getStatusTitle()}</h3>
          <Points points={trade.totalValue} type="big" />
        </div>
        
        <div className="trade-info">
          <div className="trade-detail">
            <span className="trade-label">Trade#</span>
            <span className="trade-value">{trade.tradeNumber}</span>
          </div>
          <div className="trade-detail">
            <span className="trade-label">Initiated:</span>
            <span className="trade-value">{trade.initiated}</span>
          </div>
          {/* Always show Point Fee row, even if value is 0 or undefined */}
          <div className="trade-detail">
            <span className="trade-label-with-tooltip">
              <span className="trade-label">Point Fee:</span>
              <img
                src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/tooltip.svg"
                alt="Info"
                className="fee-info-icon"
              />
              <span className="fee-tooltip">
                To maintain the long-term sustainability of our trading economy, we apply a flexible trade fee of {feePercentage}% to each completed trade.
              </span>
            </span>
            <span className="trade-value">{trade.tradeFee || 0}</span>
          </div>
          {trade.shipped && (
            <div className="trade-detail">
              <span className="trade-label">Shipped:</span>
              <span className="trade-value">{trade.shipped}</span>
            </div>
          )}
          {trade.completed && (
            <div className="trade-detail">
              <span className="trade-label">Completed:</span>
              <span className="trade-value">{trade.completed}</span>
            </div>
          )}
          {trade.cancelled && (
            <div className="trade-detail">
              <span className="trade-label">{trade.isRemovedItem ? 'Removed:' : 'Cancelled:'}</span>
              <span className="trade-value">{trade.cancelled}</span>
            </div>
          )}
          {trade.isRemovedItem && trade.removedFromTradeNumber && (
            <div className="trade-detail">
              <span className="trade-label">Removed from:</span>
              <span className="trade-value">Trade #{trade.removedFromTradeNumber}</span>
            </div>
          )}
        </div>

        {trade.status === 'ready' && (
          <ShippingCountdown initiatedDate={trade.initiatedRaw || trade.initiated} />
        )}

        {trade.status === 'transit' && trade.estimatedDelivery && (
          <div className="estimated-delivery">
            <span className="delivery-label">Estimated time to arrive</span>
            <div className="delivery-time">{trade.estimatedDelivery}</div>
            <div className="delivery-progress">
              <div className="progress-bar"></div>
            </div>
          </div>
        )}

        {trade.status === 'completed' && (
          <div className="trader-rating">
            <div className="rating-row">
              <span className="rating-label">Speed:</span>
              <span className="rating-stars">{renderStarRating(trade.speed)}</span>
            </div>
            <div className="rating-row">
              <span className="rating-label">Accuracy:</span>
              <span className="rating-stars">{renderStarRating(trade.accuracy)}</span>
            </div>
            <div className="rating-row">
              <span className="rating-label">Communication:</span>
              <span className="rating-stars">{renderStarRating(trade.communication)}</span>
            </div>
          </div>
        )}

        <div className="trade-actions">
          {trade.status === 'ready' && (
            <>
              <div className="action-buttons-row">
                <MagicButton
                  icon={
                    <img
                      src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                      alt="Contact"
                      style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                    />
                  }
                  label="Contact"
                  color="dark-grey"
                  onPress={handleContactClick}
                />
                <MagicButton
                  icon={
                    <img
                      src={`${AssetUrls.base}/assets/icons/SendCards/icon-print.svg`}
                      alt="Print"
                      style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                    />
                  }
                  label="Packing Slip"
                  color="dark-grey"
                  onPress={() => setIsPackingSlipOpen(true)}
                />
              </div>
              <MagicButton
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/SendCards/truck-in-transit.svg`}
                    alt="Truck"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label={isMarkingShipped ? "Marking..." : "Mark shipped"}
                color="purple"
                onPress={handleMarkAsShipped}
                isDisabled={isMarkingShipped}
              />
            </>
          )}
          {trade.status === 'transit' && (
            <>
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                    alt="Contact"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Contact"
                color="dark-grey"
                onPress={handleContactClick}
              />
              <MagicButton
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/SendCards/icon-print.svg`}
                    alt="Print"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Packing Slip"
                color="dark-grey"
                onPress={() => setIsPackingSlipOpen(true)}
              />
            </>
          )}
          {trade.status === 'completed' && (
            <>
              {trade.packingSlipAvailable && (
                <div className="packing-slip-expiry-wrapper">
                  <MagicButton
                    icon={
                      <img
                        src={`${AssetUrls.base}/assets/icons/SendCards/icon-print.svg`}
                        alt="Print"
                        style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                      />
                    }
                    label="Packing Slip"
                    color="dark-grey"
                    onPress={() => setIsPackingSlipOpen(true)}
                  />
                  {packingSlipDaysRemaining !== null && (
                    <span className="packing-slip-expiry-label">
                      {packingSlipDaysRemaining === 1
                        ? 'Expires today'
                        : `Available for ${packingSlipDaysRemaining} more days`}
                    </span>
                  )}
                </div>
              )}
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                    alt="Say Thanks"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Say Thanks"
                color="dark-grey"
                onPress={handleSayThanksClick}
              />
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/arevakhach-icon.svg"
                    alt="Trade Again"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Trade again"
                color="dark-grey"
                onPress={handleTradeAgain}
              />
            </>
          )}
        </div>
      </div>

      <div className="trade-card-right">
        <div className="trader-info-row">
          <div className="status-label-left">
            <span className="status-text">{getStatusLabel()}</span>
          </div>
          <div className="trader-info">
            {isReady && (
              <>
                <button
                  className="add-cards-btn"
                  onClick={() => setIsAddCardsOpen(true)}
                >
                  + Add Cards
                </button>
                <button
                  className="add-cards-btn"
                  onClick={() => setIsSendPointsOpen(true)}
                >
                  <img
                    src={AssetUrls.icons.profile.coin}
                    alt=""
                    className="send-points-btn-icon"
                  />
                  Send Points
                </button>
              </>
            )}
            <AvatarWithFlag
              avatarUrl={trade.traderAvatar}
              username={trade.traderName}
              countryCode={trade.shippingAddress?.country}
              size={38}
              isElite={trade.traderIsElite}
              showAnimatedBorder={trade.traderShowAnimatedBorder}
              borderColor1={trade.traderBorderColor1}
              borderColor2={trade.traderBorderColor2}
            />
            <div className="trader-identity">
              <UsernameLink
                username={trade.traderName}
                isElite={trade.traderIsElite}
                className="trader-name trader-name--clickable"
              />
              <Points points={trade.pointBalance} type="small" />
            </div>
          </div>
        </div>


        <div className={`items-table ${isReady ? 'items-table--editable' : ''}`}>
          <div className={`table-header ${isReady ? 'table-header--editable' : ''}`}>
            <span className="col-qty">Qty</span>
            <span className="col-name">Name</span>
            <span className="col-set">Set</span>
            <span className="col-foil">Foil</span>
            <span className="col-condition">Condition</span>
            <span className="col-language">Language</span>
            <span className="col-points">Points</span>
            {isReady && <span className="col-edit"></span>}
            <span className="col-remove"></span>
          </div>

          {trade.items.map((item, index) => {
            const isEditingThisRow = editingItemIndex === index;
            const editOption = editOptionsCache?.get(index);

            // Desktop inline edit row
            if (isEditingThisRow && editOption?.preferences && !isMobile) {
              return (
                <TradeItemEditRow
                  key={index}
                  item={item}
                  itemIndex={index}
                  editOptions={editOption}
                  onSave={handleEditSave}
                  onCancel={handleEditCancel}
                  isSaving={isUpdatingItem}
                />
              );
            }

            return (
              <div key={index} className={`table-row ${isReady ? 'table-row--editable' : ''}`}>
                <span className="col-qty">{item.qty}</span>
                <div
                  className="col-name"
                  onMouseEnter={(e) => handleCardHover(e, item)}
                  onMouseMove={(e) => handleCardHover(e, item)}
                  onMouseLeave={handleCardLeave}
                >
                  <FoilCardImage
                    src={getCardImageUrl({ image: item.image, scryfallId: item.scryfallId }, 'small')}
                    alt={item.name}
                    className="card-thumbnail"
                    foil={item.foil}
                    borderRadius="4px"
                    onError={makeCardImageErrorHandler(
                      { image: item.image, scryfallId: item.scryfallId },
                      cardImageFallbackRef,
                      index,
                      'small',
                    )}
                  />
                  <span>{item.name}</span>
                </div>
                <div className="col-set" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <SetIcon setIconUrl={item.setIconUrl} setName={item.set} />
                  <span>{item.setCode || item.set}{item.collectorNumber && <span style={{ color: '#888' }}> #{item.collectorNumber}</span>}</span>
                </div>
                <span className="col-foil">{item.foil ? 'Foil' : '—'}</span>
                <span className="col-condition">{formatCondition(item.condition)}</span>
                <span className="col-language">{formatLanguage(item.language)}</span>
                <div className="col-points">
                  <Points points={(item.pointValue || 0) * item.qty} type="small" />
                </div>
                {isReady && (
                  <div className="col-edit">
                    {isLoadingEditOptions ? (
                      <span className="edit-spinner" />
                    ) : (
                      <button
                        className="icon-btn"
                        onClick={() => !isUpdatingItem && handleEditClick(index)}
                        disabled={isUpdatingItem}
                        title="Edit item"
                      >
                        <img
                          src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/edit.svg"
                          alt="Edit"
                          width="14"
                          height="14"
                          style={{ filter: 'brightness(0) invert(1)' }}
                        />
                      </button>
                    )}
                  </div>
                )}
                <div className="col-remove">
                  {isReady && (
                    <button
                      className={`icon-btn ${isRemovingCard ? 'removing' : ''}`}
                      onClick={() => handleRemoveCard(item, index)}
                      disabled={isRemovingCard}
                      title="Remove item"
                    >
                      <svg width="18" height="18" viewBox="0 0 14 14" fill="currentColor">
                        <path d="M3.293 2.586 2.586 3.293 5.293 6l-2.707 2.707.707.707L6 6.707l2.707 2.707.707-.707L6.707 6l2.707-2.707-.707-.707L6 5.293 3.293 2.586z" transform="translate(1 1)"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile edit modal */}
        {isMobile && editingItemIndex !== null && editOptionsCache?.get(editingItemIndex)?.preferences && (
          <EditTradeItemModal
            item={trade.items[editingItemIndex]}
            editOptions={editOptionsCache.get(editingItemIndex)!}
            onSave={handleEditSave}
            onCancel={handleEditCancel}
            isSaving={isUpdatingItem}
          />
        )}
      </div>

      <PackingSlipModal
        trade={trade}
        isOpen={isPackingSlipOpen}
        onClose={() => setIsPackingSlipOpen(false)}
      />

      <ContactUserModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        receiverId={parseInt(trade.traderId)}
        receiverUsername={trade.traderName}
        receiverAvatar={trade.traderAvatar}
        tradeId={trade.id}
      />

      {isAddCardsOpen && (
        <AddCardsToTradeModal
          trade={trade}
          isOpen={isAddCardsOpen}
          onClose={() => setIsAddCardsOpen(false)}
        />
      )}

      <SendPointsModal
        isOpen={isSendPointsOpen}
        onClose={() => setIsSendPointsOpen(false)}
        recipientId={parseInt(trade.traderId)}
        recipientUsername={trade.traderName}
        tradeId={trade.id}
      />

      {hoveredCard && (
        <CardImageTooltip
          scryfallId={hoveredCard.scryfallId}
          imageUrl={hoveredCard.imageUrl}
          cardName={hoveredCard.cardName}
          position={hoveredCard.position}
          onMouseEnter={() => {}}
          onMouseLeave={handleCardLeave}
        />
      )}
    </div>
  );
}